import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import type { CreateRecordingResponseData } from "@/common/types/visits";
import {
  abandonRecordingSubmission,
  beginRecordingDraft,
  completeRecordingSubmission,
  prepareRecordingSubmission,
  type RecordingDraftBody,
  readRecordingSubmission,
} from "@/lib/recording-submission";
import { refreshOwnUsage } from "@/lib/usage-queries";
import {
  getVisitScope,
  refreshVisitReads,
  useVisitScope,
} from "@/lib/visit-queries";
import { createRecording } from "@/services/visits";
import { usePostOnboardingStore } from "@/store/postOnboarding.store";

const inFlight = new Map<string, Promise<CreateRecordingResponseData>>();
const message = (error: unknown): string =>
  error instanceof Error
    ? error.message
    : t("visit.recovery.recordingSubmissionUncertain");

export const useRecordingSubmit = ({
  visitPublicId,
}: {
  visitPublicId: string | undefined;
}) => {
  const readScope = useVisitScope();
  const account = readScope
    ? `${readScope.uid}:${readScope.accountPublicId}`
    : null;
  const scope = `${account}:${readScope?.generation}:${visitPublicId}`;
  const active = useRef<string | null>(scope);
  active.current = scope;
  const [, refresh] = useState(0);
  const [result, setResult] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    active.current = scope;
    setResult(false);
    setLoading(false);
    setError(null);
    return () => {
      active.current = null;
    };
  }, [scope]);
  const current = (): boolean =>
    active.current === scope && getVisitScope() === readScope;
  let recovery = false;
  let storageError: string | null = null;
  if (account && visitPublicId) {
    try {
      recovery =
        readRecordingSubmission(account, visitPublicId)?.kind === "pending";
    } catch (failure) {
      recovery = true;
      storageError = message(failure);
    }
  }
  const submitRecording = async (
    body: RecordingDraftBody,
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    setResult(false);
    try {
      if (!account || !visitPublicId || !readScope || !current())
        throw new Error("Sign in and select a visit before submitting.");
      const prepared = await prepareRecordingSubmission(
        account,
        visitPublicId,
        body,
      );
      if (!current()) return false;
      let operation = inFlight.get(scope);
      if (!operation) {
        operation = (async () => {
          if (getVisitScope() !== readScope)
            throw new Error("Account changed. Check the original visit.");
          if (prepared.kind === "accepted")
            return { recordingPublicId: prepared.recordingPublicId };
          const accepted = await createRecording(
            { ...body, visitPublicId, name: prepared.key },
            { key: prepared.key, userUid: readScope.uid },
          );
          if (getVisitScope() !== readScope)
            throw new Error("Account changed. Check the original visit.");
          completeRecordingSubmission(
            account,
            visitPublicId,
            prepared,
            accepted.recordingPublicId,
          );
          return accepted;
        })().finally(() => {
          inFlight.delete(scope);
        });
        inFlight.set(scope, operation);
      }
      await operation;
      if (!current()) return false;
      void refreshVisitReads(readScope);
      void refreshOwnUsage(readScope);
      usePostOnboardingStore.getState().markDone();
      setResult(true);
      refresh((value) => value + 1);
      return true;
    } catch (failure) {
      if (current()) setError(message(failure));
      return false;
    } finally {
      if (current()) {
        setLoading(false);
        refresh((value) => value + 1);
      }
    }
  };
  const beginDraft = (): boolean => {
    try {
      if (!account || !visitPublicId || !current())
        throw new Error("Sign in and select a visit before recording.");
      beginRecordingDraft(account, visitPublicId);
      setResult(false);
      setError(null);
      refresh((value) => value + 1);
      return true;
    } catch (failure) {
      setError(message(failure));
      return false;
    }
  };
  const abandon = (): void => {
    if (account && visitPublicId && current() && !inFlight.has(scope)) {
      abandonRecordingSubmission(account, visitPublicId);
      setResult(false);
      setError(null);
      refresh((value) => value + 1);
    }
  };
  return {
    submitRecording,
    beginDraft,
    abandon,
    recovery,
    result,
    loading,
    error: error ?? storageError,
  };
};
